import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Globe, ArrowRight } from "lucide-react";
import { DiscoverMenu, useDiscoverLinks } from "./DiscoverMenu";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

const links = [
  ["/ask", "nav.ask"],
  ["/explore", "nav.explore"],
  ["/projects", "nav.projects"],
  ["/property", "nav.property"],
  ["/services", "nav.services"],
  ["/experiences", "nav.experiences"],
  ["/business", "nav.business"],
  ["/partners/broker", "nav.brokers"],
  ["/partners", "nav.partners"],
];

export const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const discoverLinks = useDiscoverLinks();

  return (
    <header className="sticky top-0 z-50 glass" data-testid="main-navbar">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <span className="font-display text-xl tracking-tight text-white">BuyDubai<span className="text-gold">.ai</span></span>
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5">
          {links.map(([to, key]) => (
            <NavLink key={to} to={to} end={to === "/partners"} data-testid={`nav-${to.slice(1)}`}
              className={({ isActive }) => `px-1.5 2xl:px-3 py-2 text-[13px] 2xl:text-sm whitespace-nowrap rounded-md transition-colors duration-200 ${isActive ? "text-gold" : "text-white/70 hover:text-white"}`}>
              {t(key)}
            </NavLink>
          ))}
          <DiscoverMenu />
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={toggle} data-testid="lang-switch-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border border-white/10 text-white/80 hover:border-gold/40 transition-colors duration-200">
            <Globe size={13} />{lang === "en" ? "العربية" : "EN"}
          </button>

          {user ? (
            <div className="hidden md:flex items-center gap-1">
              <button onClick={() => nav("/dashboard")} data-testid="nav-dashboard" className="px-3 py-1.5 text-sm text-white/80 hover:text-white">{t("common.dashboard")}</button>
              <button onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn" className="px-3 py-1.5 text-sm text-white/60 hover:text-white">{t("common.logout")}</button>
            </div>
          ) : (
            <Link to="/auth" data-testid="nav-signin" className="hidden md:inline-block px-3 py-1.5 text-sm whitespace-nowrap text-white/80 hover:text-white">{t("common.sign_in")}</Link>
          )}

          <Link to="/ask" data-testid="nav-ask-cta" className="hidden md:inline-flex items-center gap-1.5 px-4 py-1.5 text-sm whitespace-nowrap rounded-full bg-gold text-ink font-semibold hover:brightness-110 transition-all duration-200">
            {t("common.ask_ai")} <ArrowRight size={14} />
          </Link>

          <button className="xl:hidden text-white p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden glass border-t border-white/10 px-5 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          {discoverLinks.length > 0 && (
            <>
              <div className="text-[11px] uppercase tracking-[0.18em] text-gold/70 pt-1">{t("nav.discover_h")}</div>
              {discoverLinks.map(([to, key]) => (
                <NavLink key={to} to={to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${to.slice(1)}`} className="py-2 text-white/80 hover:text-gold">{t(key)}</NavLink>
              ))}
              <div className="border-t border-white/10 my-1" />
            </>
          )}
          {links.map(([to, key]) => (
            <NavLink key={to} to={to} onClick={() => setOpen(false)} className="py-2 text-white/80 hover:text-gold">{t(key)}</NavLink>
          ))}
          <Link to="/ask" onClick={() => setOpen(false)} className="py-2 text-gold font-semibold">{t("common.ask_ai")}</Link>
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="py-2 text-white/80">{t("common.dashboard")}</Link>
              <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="py-2 text-left text-white/60">{t("common.logout")}</button>
            </>
          ) : (
            <Link to="/auth" onClick={() => setOpen(false)} className="py-2 text-white/80">{t("common.sign_in")}</Link>
          )}
        </div>
      )}
    </header>
  );
};
